<template>
  <div class="wcost">
    <!-- Cabeçalho -->
    <div class="wcost-head">
      <div class="wcost-head-title">
        <span class="wcost-ico">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-9 8.4 8.5 8.5 0 0 1-3.9-.9L3 20l1-3.1A8.38 8.38 0 0 1 12 3a8.5 8.5 0 0 1 9 8.5Z" />
          </svg>
        </span>
        <div>
          <h4 class="wcost-title">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.TITLE') }}</h4>
          <p v-if="estimate && estimate.disponivel" class="wcost-sub">
            {{ $t('GENERAL_SETTINGS.WHATSAPP_COST.PERIOD') }}: <b>{{ estimate.periodo.rotulo }}</b>
          </p>
        </div>
      </div>
      <div class="wcost-head-actions">
        <select
          v-if="showInboxSelect"
          v-model="selectedInbox"
          class="wcost-select"
          :aria-label="$t('GENERAL_SETTINGS.WHATSAPP_COST.INBOX')"
        >
          <option value="all">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.ALL_INBOXES') }}</option>
          <option v-for="(n, i) in estimate.numeros" :key="i" :value="i">{{ n.caixa }}</option>
        </select>
        <button type="button" class="wcost-refresh" :disabled="loading" @click="load">
          {{ loading ? $t('GENERAL_SETTINGS.WHATSAPP_COST.LOADING') : $t('GENERAL_SETTINGS.WHATSAPP_COST.REFRESH') }}
        </button>
      </div>
    </div>

    <!-- Carregando -->
    <div v-if="loading" class="wcost-state">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.LOADING') }}</div>

    <!-- Erro -->
    <div v-else-if="error" class="wcost-state wcost-state--error">
      {{ $t('GENERAL_SETTINGS.WHATSAPP_COST.ERROR') }}
    </div>

    <!-- Indisponível (sem caixa WhatsApp Cloud) -->
    <div v-else-if="estimate && !estimate.disponivel" class="wcost-state">
      {{ estimate.motivo === 'pending'
        ? $t('GENERAL_SETTINGS.WHATSAPP_COST.PENDING')
        : $t('GENERAL_SETTINGS.WHATSAPP_COST.NO_CHANNEL') }}
    </div>

    <!-- Estimativa (layout horizontal: painel à esquerda + tabela à direita) -->
    <template v-else-if="estimate">
      <div class="wcost-body">
        <aside class="wcost-hero">
          <span class="wcost-hero-label">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.ESTIMATED_TOTAL') }}</span>
          <span class="wcost-hero-brl">{{ brl(active.totais.custo_brl) }}</span>
          <span class="wcost-hero-usd">≈ {{ usd(active.totais.custo_usd) }}</span>
          <div class="wcost-hero-meta">
            <div class="wcost-metric">
              <span class="wcost-metric-num">{{ active.totais.cobraveis }}</span>
              <span class="wcost-metric-lbl">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.BILLABLE_MSGS') }}</span>
            </div>
            <div class="wcost-metric">
              <span class="wcost-metric-num">{{ active.totais.mensagens }}</span>
              <span class="wcost-metric-lbl">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.TOTAL_MSGS') }}</span>
            </div>
          </div>
          <div v-if="estimate.cambio" class="wcost-fx">
            {{ $t('GENERAL_SETTINGS.WHATSAPP_COST.FX') }}:
            <b>{{ estimate.cambio.par }} {{ fmt(estimate.cambio.taxa) }}</b>
            <span class="wcost-muted">{{ estimate.cambio.fonte }}</span>
          </div>
        </aside>

        <div class="wcost-main">
          <table v-if="active.status === 'ok'" class="wcost-table">
            <thead>
              <tr>
                <th>{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.CATEGORY') }}</th>
                <th class="ta-r">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.VOLUME') }}</th>
                <th class="ta-r">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.RATE') }}</th>
                <th class="ta-r">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.COST') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in active.categorias" :key="c.categoria">
                <td>
                  {{ c.rotulo }}
                  <span class="wcost-chip" :class="c.cobravel ? 'is-bill' : 'is-free'">
                    {{ c.cobravel ? $t('GENERAL_SETTINGS.WHATSAPP_COST.BILLABLE') : $t('GENERAL_SETTINGS.WHATSAPP_COST.FREE') }}
                  </span>
                </td>
                <td class="ta-r wcost-num">{{ c.volume }}</td>
                <td class="ta-r wcost-num wcost-muted">{{ c.cobravel ? usd(c.tarifa_usd) : '—' }}</td>
                <td class="ta-r wcost-num">{{ c.custo_usd > 0 ? brl(c.custo_usd * fxRate) : '—' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="wcost-state">
            {{ active.status === 'erro'
              ? $t('GENERAL_SETTINGS.WHATSAPP_COST.NO_RESPONSE')
              : $t('GENERAL_SETTINGS.WHATSAPP_COST.NO_DATA_YET') }}
          </div>
        </div>
      </div>

      <!-- Por caixa/número (visão geral: só quando o filtro está em "Todas") -->
      <div v-if="selectedInbox === 'all' && estimate.numeros && estimate.numeros.length" class="wcost-numeros">
        <div class="wcost-numeros-head">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.BY_NUMBER') }}</div>
        <table class="wcost-table">
          <thead>
            <tr>
              <th>{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.INBOX') }}</th>
              <th>{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.NUMBER') }}</th>
              <th class="ta-r">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.BILLABLE_TOTAL') }}</th>
              <th class="ta-r">{{ $t('GENERAL_SETTINGS.WHATSAPP_COST.COST') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(n, i) in estimate.numeros" :key="i">
              <td>{{ n.caixa }}</td>
              <td class="wcost-muted">{{ n.numero }}</td>
              <template v-if="n.status === 'ok'">
                <td class="ta-r wcost-num">{{ n.cobraveis }}<span class="wcost-muted"> / {{ n.volume }}</span></td>
                <td class="ta-r wcost-num">{{ brl(n.custo_brl) }}</td>
              </template>
              <td v-else class="wcost-pending" colspan="2">
                {{ n.status === 'erro'
                  ? $t('GENERAL_SETTINGS.WHATSAPP_COST.NO_RESPONSE')
                  : $t('GENERAL_SETTINGS.WHATSAPP_COST.NO_DATA_YET') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Disclaimer / observações (full-width) -->
      <div class="wcost-note">
        <ul>
          <li v-for="(o, i) in estimate.observacoes" :key="i">{{ o }}</li>
        </ul>
        <p class="wcost-rates-src">{{ estimate.tarifas_fonte }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import WhatsappCostEstimateAPI from 'dashboard/api/whatsappCostEstimate';

export default {
  name: 'WhatsappCostCard',
  data() {
    return { loading: true, error: false, estimate: null, selectedInbox: 'all' };
  },
  computed: {
    fxRate() {
      return this.estimate && this.estimate.cambio ? this.estimate.cambio.taxa : null;
    },
    // Mostra o select só quando há mais de uma caixa para escolher.
    showInboxSelect() {
      return !!(this.estimate && this.estimate.disponivel && this.estimate.numeros && this.estimate.numeros.length > 1);
    },
    // Escopo ativo: a conta inteira ("Todas") ou a caixa selecionada.
    active() {
      const e = this.estimate;
      if (!e || !e.disponivel) return { categorias: [], totais: {}, status: 'ok' };
      const n = e.numeros && e.numeros[this.selectedInbox];
      if (this.selectedInbox === 'all' || !n) {
        return { categorias: e.categorias, totais: e.totais, status: 'ok' };
      }
      return {
        categorias: n.categorias || [],
        totais: {
          mensagens: n.volume,
          cobraveis: n.cobraveis,
          custo_usd: n.custo_usd,
          custo_brl: n.custo_brl,
        },
        status: n.status,
      };
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await WhatsappCostEstimateAPI.get();
        this.estimate = data;
        this.selectedInbox = 'all';
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    brl(v) {
      if (v === null || v === undefined) return '—';
      return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    usd(v) {
      if (v === null || v === undefined) return '—';
      return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    fmt(v) {
      return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
  },
};
</script>

<style lang="scss" scoped>
.wcost {
  width: 100%;
  border: 1px solid var(--s-100);
  border-radius: 12px;
  background: var(--color-surface);
  overflow: hidden;
}
.wcost-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);
  border-bottom: 1px solid var(--s-75);
}
.wcost-head-title { display: flex; align-items: center; gap: var(--space-small); }
.wcost-ico {
  width: 2rem;
  height: 2rem;
  color: var(--g-600);
  flex-shrink: 0;
  svg { width: 100%; height: 100%; }
}
.wcost-title {
  margin: 0;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--s-900);
}
.wcost-sub { margin: 2px 0 0; font-size: var(--font-size-mini); color: var(--s-500); }
.wcost-head-actions {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  flex-shrink: 0;
}
.wcost-select {
  height: auto;
  margin: 0;
  border: 1px solid var(--s-200);
  background: var(--color-surface);
  color: var(--s-800);
  border-radius: 8px;
  padding: var(--space-smaller) var(--space-small);
  font-size: var(--font-size-mini);
  max-width: 16rem;
}
.wcost-refresh {
  border: 1px solid var(--s-200);
  background: var(--color-surface);
  color: var(--s-700);
  border-radius: 8px;
  padding: var(--space-smaller) var(--space-small);
  font-size: var(--font-size-mini);
  cursor: pointer;
  &:hover { background: var(--s-25); }
  &:disabled { opacity: 0.6; cursor: default; }
}
.wcost-state {
  padding: var(--space-large);
  text-align: center;
  color: var(--s-500);
  font-size: var(--font-size-small);
  &--error { color: var(--r-600); }
}

/* Corpo horizontal: painel de total à esquerda, tabela à direita */
.wcost-body {
  display: flex;
  align-items: stretch;
  gap: var(--space-normal);
  padding: var(--space-normal);
  flex-wrap: wrap;
}
.wcost-hero {
  flex: 0 0 15rem;
  display: flex;
  flex-direction: column;
  gap: var(--space-micro);
  padding: var(--space-normal);
  border-radius: 10px;
  background: linear-gradient(160deg, var(--g-50), var(--white));
  border: 1px solid var(--g-100);
}
.wcost-hero-label {
  font-size: var(--font-size-mini);
  color: var(--s-500);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.wcost-hero-brl {
  font-size: 2.2rem;
  font-weight: var(--font-weight-bold);
  color: var(--g-800);
  line-height: 1.05;
}
.wcost-hero-usd { font-size: var(--font-size-small); color: var(--s-500); }
.wcost-hero-meta {
  display: flex;
  gap: var(--space-normal);
  margin-top: var(--space-small);
  padding-top: var(--space-small);
  border-top: 1px solid var(--g-100);
}
.wcost-metric { display: flex; flex-direction: column; }
.wcost-metric-num {
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  color: var(--s-800);
}
.wcost-metric-lbl { font-size: var(--font-size-mini); color: var(--s-500); }
.wcost-fx {
  margin-top: auto;
  padding-top: var(--space-small);
  font-size: var(--font-size-mini);
  color: var(--s-600);
  b { display: block; }
  .wcost-muted { display: block; }
}

.wcost-main { flex: 1 1 22rem; min-width: 0; }
.wcost-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-small);
  th, td {
    padding: var(--space-small) var(--space-small);
    border-bottom: 1px solid var(--s-50);
    text-align: left;
  }
  thead th { border-bottom: 1px solid var(--s-100); }
  th {
    font-size: var(--font-size-mini);
    color: var(--s-500);
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  tbody tr:last-child td { border-bottom: 0; }
  .ta-r { text-align: right; }
}
.wcost-num { font-variant-numeric: tabular-nums; color: var(--s-800); }
.wcost-muted { color: var(--s-400); }
.wcost-chip {
  display: inline-block;
  margin-left: var(--space-smaller);
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  &.is-bill { background: var(--y-50); color: var(--y-800); }
  &.is-free { background: var(--g-50); color: var(--g-700); }
}
.wcost-numeros {
  padding: 0 var(--space-normal) var(--space-small);
  border-top: 1px solid var(--s-50);
}
.wcost-numeros-head {
  font-size: var(--font-size-mini);
  color: var(--s-500);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: var(--space-small) 0 var(--space-smaller);
}
.wcost-pending {
  text-align: right;
  font-size: var(--font-size-mini);
  color: var(--s-400);
  font-style: italic;
}
.wcost-note {
  padding: var(--space-small) var(--space-normal) var(--space-normal);
  background: var(--y-50);
  border-top: 1px solid var(--s-50);
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-micro) var(--space-normal);
  }
  li {
    position: relative;
    padding-left: var(--space-normal);
    font-size: var(--font-size-mini);
    color: var(--y-900);
    flex: 1 1 18rem;
    &::before { content: '•'; position: absolute; left: var(--space-smaller); }
  }
}
.wcost-rates-src {
  margin: var(--space-smaller) 0 0;
  font-size: var(--font-size-mini);
  color: var(--s-500);
}

/* Tema escuro: superfícies "papel" (--white -> --color-surface, acima) e tons
   de marca verde/amarelo que não flipam sozinhos + contraste de texto. */
:root[data-theme='dark'] {
  .wcost-hero {
    background: linear-gradient(160deg, rgba(0, 196, 29, 0.12), var(--color-surface));
    border-color: var(--color-border);
  }
  .wcost-hero-brl { color: var(--g-400); }
  .wcost-hero-meta { border-top-color: var(--color-border); }
  .wcost-chip.is-bill { background: rgba(246, 216, 25, 0.16); color: var(--y-300); }
  .wcost-chip.is-free { background: rgba(68, 206, 75, 0.16); color: var(--g-300); }
  .wcost-note {
    background: rgba(246, 216, 25, 0.08);
    border-top-color: var(--color-border);
    li { color: var(--color-ink); }
  }
  .wcost-rates-src { color: var(--color-ink-soft); }
  .wcost-select,
  .wcost-refresh { border-color: var(--color-border); color: var(--color-ink); }
}
</style>
